import {assert} from 'Asserts';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import {
  CreateMixtureGroupExtras,
  MixtureGroupMetadata,
} from 'notebook/integrations/registry/MixtureGroupConstants';
import {MixtureMetadataStructuredTable} from 'notebook/integrations/registry/MixtureMetadataStructuredTableAdapter';
import {MixtureNoteLinkedGroupAdapter} from 'notebook/integrations/registry/MixtureNoteLinkedGroupAdapter';
import {
  doPostRegistrationRefresh,
  RegistrationActionOrigin,
  runRegistrationFlow,
} from 'notebook/integrations/registry/RegistrationActionUtil';
import {
  MixtureGroupResponse,
  submitMixtureGroup,
} from 'notebook/notes/groups/action-functions/GroupActionFunctionDAO';
import makeSimpleGroupActionFunction from 'notebook/notes/groups/action-functions/makeSimpleGroupActionFunction';
import {
  GroupActionFunctionParameters,
  NoteLinkedGroup,
  NoteLinkedGroupAdapter,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import isGroupActionButtonDisabled, {
  getGroupActionButtonDisabledReason,
  getGroupActionButtonDisabledTooltip,
  getGroupActionButtonTableStatus,
  GroupActionDisabledReason,
} from 'notebook/notes/groups/util/GroupActionButtonUtil';
import {ACTION_SUBMIT_MIXTURE_PREP} from 'notebook/store/VersioningConstants';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {
  getSubmitButtonHidden,
  getSubmitButtonLabel,
  getSubmitButtonStyle,
  getSubmitIconForTableStatus,
} from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {performServerSideTableWriteActionWithAudits} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import invokeStructuredTableMappings from 'notebook/structured-tables/structured-table-mappings/invokeStructuredTableMappings';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  StructuredTableKind,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {OutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';

import {findNotePartForStructuredTable} from '../util/ContainedNotePartUtil';

export async function onInvokeSubmitMixturePrepActionFunction({
  entryOrTemplateId,
  noteLinkedGroup,
  evaluatedTableMap,
  actionFunctionContext,
  noteLinkedGroupAdapter,
}: {
  entryOrTemplateId: string;
  noteLinkedGroup: NoteLinkedGroup<MixtureGroupMetadata>;
  evaluatedTableMap: OutputTableMap;
  actionFunctionContext: ActionFunctionContext;
  noteLinkedGroupAdapter: NoteLinkedGroupAdapter<MixtureGroupMetadata, CreateMixtureGroupExtras>;
}): Promise<void> {
  const tagSchema = (noteLinkedGroupAdapter as MixtureNoteLinkedGroupAdapter).getTagSchema(noteLinkedGroup);
  assert(tagSchema != null, 'Tried to submit mixture group with inaccessible or nonexistent schema');

  const structuredTables = noteLinkedGroupAdapter.getContainedStructuredTables(noteLinkedGroup);
  const computedTables = structuredTables.map((structuredTable) => evaluatedTableMap[structuredTable.name]);
  const structuredTableAdapters = structuredTables.map(
    (structuredTable) => getStructuredTableAdapters()[structuredTable.kind]
  );

  await performServerSideTableWriteActionWithAudits<MixtureGroupResponse>({
    entryId: entryOrTemplateId,
    loadingScreenMessage: 'Submitting...',
    autoSaverAction: ACTION_SUBMIT_MIXTURE_PREP,
    doPerformAction: ({dataModsHeader}) =>
      runRegistrationFlow(
        (allowMerging: boolean) =>
          submitMixtureGroup(
            entryOrTemplateId,
            noteLinkedGroup,
            evaluatedTableMap,
            actionFunctionContext,
            {
              allowMerging,
            },
            dataModsHeader
          ),
        tagSchema,
        RegistrationActionOrigin.MIXTURE_GROUP
      ),
    doPostRefresh: async (actionResult) => {
      const metadataNotePart = findNotePartForStructuredTable<MixtureMetadataStructuredTable>(
        noteLinkedGroup,
        StructuredTableKind.MIXTURE_METADATA
      );
      assert(
        metadataNotePart != null,
        'Expected mixture group to contain a NotePart corresponding to the mixture metadata structured table'
      );
      await doPostRegistrationRefresh(
        actionResult.responseData,
        entryOrTemplateId,
        metadataNotePart.item.provenanceUuid,
        tagSchema
      );
      // Populate any tables mapped out of this Mixture Prep group. The contained tables captured above
      // still hold their pre-submit (unfinalized) rows, so the mapping picks up the newly submitted rows.
      invokeMixtureGroupTableMappings(entryOrTemplateId, structuredTables as Array<UnknownStructuredTable>);
    },
    analyticsActionForPerfTracking: ELN_ANALYTICS_ACTIONS.SUBMITTED_MIXTURE_GROUP.action,
    structuredTables,
    structuredTableAdapters,
    computedTables,
    tables: [],
  });
}

/**
 * Triggers Table Mapping out of a Mixture Prep group's contained structured tables after the group is
 * submitted. Each contained table (metadata and ingredients) may be a mapping source. The tables must still
 * hold their pre-submit (unfinalized) rows so that only the newly submitted rows are mapped.
 *
 * `invokeStructuredTableMappings` no-ops for source tables that have no configured mappings, so this is safe
 * to call for every contained table.
 */
export function invokeMixtureGroupTableMappings(
  entryOrTemplateId: string,
  structuredTables: Array<UnknownStructuredTable>
): void {
  for (const sourceTable of structuredTables) {
    invokeStructuredTableMappings(entryOrTemplateId, sourceTable);
  }
}

export const RESYNC_REQUIRED_SUBMIT_TOOLTIP = 'Re-sync the group before submitting';

function isSubmitMixtureGroupDisabled(
  params: GroupActionFunctionParameters<MixtureGroupMetadata, CreateMixtureGroupExtras>
): boolean {
  return isGroupActionButtonDisabled(params);
}

function getSubmitMixtureGroupTooltip(
  params: GroupActionFunctionParameters<MixtureGroupMetadata, CreateMixtureGroupExtras>
): string | null {
  // Defer to the shared tooltip when an existing disable reason already applies, so we don't
  // mask higher-priority signals (locked entry, validation errors, etc.).
  const disabledReason = getGroupActionButtonDisabledReason(params);
  if (disabledReason === GroupActionDisabledReason.GROUP_RESYNC_REQUIRED) {
    return RESYNC_REQUIRED_SUBMIT_TOOLTIP;
  }
  if (disabledReason != null) {
    return getGroupActionButtonDisabledTooltip(params);
  }
  return null;
}

const submitMixtureGroupActionFunction = makeSimpleGroupActionFunction<
  MixtureGroupMetadata,
  CreateMixtureGroupExtras
>({
  label: (params) => getSubmitButtonLabel(getGroupActionButtonTableStatus(params)),
  icon: (params) =>
    getSubmitIconForTableStatus(getGroupActionButtonTableStatus(params), FolderItemIcon.Mixture),
  buttonDesign: {
    buttonStyle: (params) =>
      getSubmitButtonStyle(getGroupActionButtonTableStatus(params), ButtonStyle.SECONDARY),
    showTextOnButton: true,
  },
  onInvoke: onInvokeSubmitMixturePrepActionFunction,
  getDisabled: isSubmitMixtureGroupDisabled,
  getTooltip: getSubmitMixtureGroupTooltip,
  getHidden: (params) => getSubmitButtonHidden(getGroupActionButtonTableStatus(params), params.isCollapsed),
});

export default submitMixtureGroupActionFunction;
